/**
 * Shuffles an array using the Fisher-Yates algorithm.
 * Creates a new array and does not modify the original.
 *
 * @param array - Array to shuffle
 * @returns New shuffled array
 *
 * @example
 * ```ts
 * const choices = ["cat", "hat", "bat", "mat"];
 * const shuffled = shuffleArray(choices);
 * // shuffled might be ["bat", "cat", "mat", "hat"]
 * // original choices is unchanged
 * ```
 */
export function shuffleArray<T>(array: T[]): T[] {
  return shuffled(array);
}

/** Fisher-Yates over a copy with an injectable source, so a seeded source replays the same order. */
export function shuffled<T>(
  items: readonly T[],
  random: () => number = Math.random,
): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    // `!` rather than a guard: a guard would skip swaps for arrays that hold undefined.
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}
